
import { Component, Vue } from "vue-property-decorator";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import { PaymentMethodType } from "@/models/Subscriptions/Payment/PaymentMethodType";
import vModal from "@/components/vModal.vue";
import vTable from "@/components/vTable.vue";
import vPreLoader from "@/components/vPreLoader.vue";
import { BraintreeCountries } from "@/utils/BraintreeCountries";
import VueTelInput from "vue-tel-input";

Vue.use(VueTelInput);

let dropin = require("braintree-web-drop-in");

@Component({
  components: {
    vBreadcrumbs,
    vTable,
    vPreLoader,
    vModal,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
})
class ManagePayment extends Vue {
  localizationState: any;
  accountStoreState: any;

  //////////
  // Data
  //////////
  creditCards: any[] = [];
  payPalAccounts: any[] = [];

  braintreeCountries: string[] = BraintreeCountries;

  showPaymentPreloader = false;
  showBillingAddress = false;
  dropinInstance: any = {};

  creatingNewPaymentMethod = {
    isDefault: false,
    modal: {
      isOpen: false,
    },
    billingAddress: {
      country: "",
      state: "",
      postalCode: "",
      city: "",
      addressLine: "",
      phoneNumber: "",
      firstName: "",
      lastName: "",
    },
    paymentMethodNonce: "",
    cardholderName: "",
  };

  updatingPaymentMethod = {
    modal: {
      isOpen: false,
    },
    billingAddress: {
      country: "",
      state: "",
      postalCode: "",
      city: "",
      addressLine: "",
      phoneNumber: "",
      firstName: "",
      lastName: "",
    },
    id: 0,
    cardholderName: "",
    expiryMonth: "01",
    expiryYear: "",
  };

  removingPaymentMethod = {
    id: 0,
    modal: {
      isOpen: false,
    },
  };

  creditCard = {
    isOpen: false,
    paymentMethodType: PaymentMethodType.CreditCard,
    token: "",
    isDafault: false,
    imageUrl: "",
    billingAddress: {
      country: "",
      state: "",
      postalCode: "",
      city: "",
      addressLine: "",
      phoneNumber: "",
      firstName: "",
      lastName: "",
    },
    details: {
      cardType: "",
      cardholderName: "",
      expiryMonth: "",
      expiryYear: "",
      lastFour: "",
      maskedNumber: "",
    },
  };

  payPal = {
    isOpen: false,
    paymentMethodType: PaymentMethodType.PayPal,
    token: "",
    isDafault: false,
    imageUrl: "",
    billingAddress: {
      country: "",
      state: "",
      postalCode: "",
      city: "",
      addressLine: "",
      phoneNumber: "",
      firstName: "",
      lastName: "",
    },
    details: {
      email: "",
    },
  };

  //////////
  // Computed
  //////////

  get translations() {
    return this.localizationState.translations;
  }

  //////////
  // Methods
  //////////

  // Get next 10 years from todays date
  getNextTenYears() {
    const years = [];
    const todaysDate = new Date();
    let year = todaysDate.getFullYear();
    const lastYear = year + 11;

    for (year; year < lastYear; year++) {
      years.push(year);
    }
    return years;
  }

  // Create new payment method
  createNewPaymentMethod() {
    this.dropinInstance.requestPaymentMethod((err: any, payload: any) => {
      if (err) {
        this.$snotify.error("Please check payment info.");
        return;
      }

      let createPaymentMethod = {
        IsDefault: this.creatingNewPaymentMethod.isDefault,
        BillingAddress: {
          Country: this.creatingNewPaymentMethod.billingAddress.country,
          State: this.creatingNewPaymentMethod.billingAddress.state,
          PostalCode: this.creatingNewPaymentMethod.billingAddress.postalCode,
          City: this.creatingNewPaymentMethod.billingAddress.city,
          AddressLine: this.creatingNewPaymentMethod.billingAddress.addressLine,
          PhoneNumber: this.creatingNewPaymentMethod.billingAddress.phoneNumber,
          FirstName: this.creatingNewPaymentMethod.billingAddress.firstName,
          LastName: this.creatingNewPaymentMethod.billingAddress.lastName,
        },
        PaymentMethodNonce: payload.nonce,
      };

      // @ts-ignore
      this.$snotify.async(
        "Creating payment method..",
        () =>
          new Promise<void>((resolve, reject) => {
            SubscriptionRepository.createPaymentMethod(createPaymentMethod)
              .then(() => {
                this.$snotify.success(
                  "Successfully created new payment method."
                );
                if (this.$route.query.returnUrl) {
                  let returnUrl: string = <string>this.$route.query.returnUrl;
                  this.$router.push({ path: returnUrl });
                }
                else {
                  this.creatingNewPaymentMethod.modal.isOpen = false;
                  this.loadPaymentMethods();
                }
                resolve();
              })
              .catch((error) => {
                if (error.response)
                  this.$snotify.error("Error.");
                else
                  this.$snotify.error(this.translations.get("network-error"));
                reject();
              });
          }),
        {
          timeout: 1000,
          closeOnClick: true,
        }
      );
    });
  }

  //  Update payment method
  updatePaymentMethod() {
    let updatePaymentMethod = {
      Id: this.updatingPaymentMethod.id,
      CardholderName: this.updatingPaymentMethod.cardholderName,
      ExpiryMonth: this.updatingPaymentMethod.expiryMonth,
      ExpiryYear: this.updatingPaymentMethod.expiryYear,
      BillingAddress: {
        Country: this.updatingPaymentMethod.billingAddress.country,
        State: this.updatingPaymentMethod.billingAddress.state,
        PostalCode: this.updatingPaymentMethod.billingAddress.postalCode,
        City: this.updatingPaymentMethod.billingAddress.city,
        AddressLine: this.updatingPaymentMethod.billingAddress.addressLine,
        PhoneNumber: this.updatingPaymentMethod.billingAddress.phoneNumber,
        FirstName: this.updatingPaymentMethod.billingAddress.firstName,
        LastName: this.updatingPaymentMethod.billingAddress.lastName,
      },
    };

    // @ts-ignore
    this.$snotify.async(
      "Updating credit card..",
      () =>
        new Promise<void>((resolve, reject) => {
          SubscriptionRepository.updatePaymentMethod(updatePaymentMethod)
            .then(() => {
              this.$snotify.success("Credit card successfully updated.");
              if (this.$route.query.returnUrl) {
                let returnUrl: string = <string>this.$route.query.returnUrl;
                this.$router.push({ path: returnUrl });
              }
              else {
                this.updatingPaymentMethod.modal.isOpen = false;
                this.loadPaymentMethods();
              }
              resolve();
            })
            .catch((error) => {
              if (error.response)
                this.$snotify.error(
                  error.response.errorMessages[0].userVisibleMessage
                );
              else
                this.$snotify.error(this.translations.get("network-error"));
              reject();
            });
        }),
      {
        timeout: 1000,
        closeOnClick: true,
      }
    );
  }

  // Delete payment method
  removePaymentMethod() {
    // @ts-ignore
    this.$snotify.async(
      "Deleting payment method..",
      () =>
        new Promise<void>((resolve, reject) => {
          SubscriptionRepository.deletePaymentMethod({
            paymentMethodId: this.removingPaymentMethod.id,
          })
            .then(() => {
              this.$snotify.success("Successfully deleted.");
              if (this.$route.query.returnUrl) {
                let returnUrl: string = <string>this.$route.query.returnUrl;
                this.$router.push({ path: returnUrl });
              }
              else {
                this.removingPaymentMethod.modal.isOpen = false;
                this.loadPaymentMethods();
              }
              resolve();
            })
            .catch((error) => {
              if (error.response)
                this.$snotify.error(
                  error.response.errorMessages[0].userVisibleMessage
                );
              else
                this.$snotify.error(this.translations.get("network-error"));
              reject();
            });
        }),
      {
        timeout: 1000,
        closeOnClick: true,
      }
    );
  }

  // Dropin instance initialization
  initDropinInstance() {
    SubscriptionRepository.generateClientToken().then((response) => {
      dropin.create(
        {
          authorization: response.data.result,
          container: document.getElementById("dropin"),
          paypal: {
            flow: "vault",
          },
          card: {
            cardholderName: {
              required: true,
            },
          },
        },
        (error: any, dropinInstance: any) => {
          if (error) {
            return;
          }
          this.dropinInstance = dropinInstance;
          this.showPaymentPreloader = false;
        }
      );
    });
  }

  // Open create payment method modal
  openNewCreditCardModal() {
    this.showPaymentPreloader = true;
    this.creatingNewPaymentMethod.isDefault = false;
    this.creatingNewPaymentMethod.billingAddress.country = "";
    this.creatingNewPaymentMethod.billingAddress.state = "";
    this.creatingNewPaymentMethod.billingAddress.postalCode = "";
    this.creatingNewPaymentMethod.billingAddress.city = "";
    this.creatingNewPaymentMethod.billingAddress.addressLine = "";
    this.creatingNewPaymentMethod.billingAddress.phoneNumber = "";
    this.creatingNewPaymentMethod.billingAddress.firstName = "";
    this.creatingNewPaymentMethod.billingAddress.lastName = "";

    this.creatingNewPaymentMethod.modal.isOpen = true;
    this.initDropinInstance();
  }

  // Open update payment method modal
  openEditCreditCardModal(id: number) {
    let card = this.creditCards.find((x: any) => x.id == id);
    if (card) {
      this.updatingPaymentMethod.id = card.id;
      this.updatingPaymentMethod.cardholderName = card.details.cardholderName;
      this.updatingPaymentMethod.expiryMonth = card.details.expiryMonth;
      this.updatingPaymentMethod.expiryYear = card.details.expiryYear;
      this.updatingPaymentMethod.billingAddress.country =
        card.billingAddress.country;
      this.updatingPaymentMethod.billingAddress.state =
        card.billingAddress.state;
      this.updatingPaymentMethod.billingAddress.postalCode =
        card.billingAddress.postalCode;
      this.updatingPaymentMethod.billingAddress.city = card.billingAddress.city;
      this.updatingPaymentMethod.billingAddress.addressLine =
        card.billingAddress.addressLine;
      this.updatingPaymentMethod.billingAddress.phoneNumber =
        card.billingAddress.phoneNumber;
      this.updatingPaymentMethod.billingAddress.firstName =
        card.billingAddress.firstName;
      this.updatingPaymentMethod.billingAddress.lastName =
        card.billingAddress.lastName;
    }

    this.updatingPaymentMethod.modal.isOpen = true;
  }

  // Delete payment method modal
  openDeletePaymentMethodModal(id: number) {
    this.removingPaymentMethod.id = id;
    this.removingPaymentMethod.modal.isOpen = true;
  }

  // Set payment method as default
  setAsDefault(card: any) {
    let paymentMethodId = card.id;
    SubscriptionRepository.markPaymentMethodAsDefault(paymentMethodId)
      .then(() => {
        this.$snotify.success("Successfully marked as default.");
        this.loadPaymentMethods();
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  // Load all payment methods
  loadPaymentMethods() {
    SubscriptionRepository.listPaymentMethods()
      .then((response) => {
        let cards = response.data.result.filter(
          (x: any) => x.paymentMethodType == 0
        );

        this.creditCards = cards.map((x: any) => {
          return {
            isOpen: false,
            id: x.id,
            paymentMethodType: x.paymentMethodType,
            token: x.token,
            isDefault: x.isDefault,
            imageUrl: x.imageUrl,
            details: x.details,
            billingAddress: x.billingAddress,
          };
        });

        let payPals = response.data.result.filter(
          (x: any) => x.paymentMethodType == 1
        );

        this.payPalAccounts = payPals.map((x: any) => {
          return {
            isOpen: false,
            id: x.id,
            paymentMethodType: x.paymentMethodType,
            token: x.token,
            isDefault: x.isDefault,
            imageUrl: x.imageUrl,
            details: x.details,
            billingAddress: x.billingAddress,
          };
        });
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  // Get billing address information formated
  billingAddressInfo(card: any) {
    let country = "";
    let state = "";
    let city = "";
    let postalCode = "";
    let fullInfo = "";

    if (card.billingAddress != null) {
      fullInfo = [country, state, city, postalCode]
        .filter((x) => x != null && x != "")
        .join(", ");
    }
    return fullInfo;
  }

  toggleCreditCard(card: any) {
    let creditCard = this.creditCards.find((x: any) => x.id == card.id);
    if (creditCard) {
      if (card.isOpen == false) {
        //@ts-ignore
        creditCard.isOpen = true;
      }
      else if (card.isOpen == true) {
        //@ts-ignore
        creditCard.isOpen = false;
      }
    }
  }

  togglePayPal(pp: any) {
    let payPal = this.payPalAccounts.find((x: any) => x.id == pp.id);
    if (payPal) {
      if (pp.isOpen == false) {
        //@ts-ignore
        payPal.isOpen = true;
      }
      else if (pp.isOpen == true) {
        //@ts-ignore
        payPal.isOpen = false;
      }
    }
  }

  toggleBillingAddress() {
    return (this.showBillingAddress = !this.showBillingAddress);
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common", "managePayment"],
    });

    this.loadPaymentMethods();
  }
}
export default ManagePayment;
